import { useEffect, useState } from 'react';
import { ShoppingCart, Check, Copy, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { invInput, invField, money, qty, errMsg } from './invApi';

const SupplierHints = ({ sups }) => (
  <div className="flex flex-wrap gap-2">
    {sups.length ? sups.map((s, i) => (
      <span key={i} className="text-[11px] bg-slate-800 rounded-full px-2.5 py-1 text-slate-300">
        {s.supplier_name} · {money(s.last_cost)}{s.avg_lead_days != null ? ` · ${s.avg_lead_days} j` : ''}{!s.has_account ? ' · aucun compte' : ''}
        {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="ml-1 text-sky-400 underline">site</a>}
      </span>
    )) : <span className="text-[11px] text-slate-500">Aucun fournisseur connu pour cette pièce</span>}
  </div>
);

const SuggestionRow = ({ s, pick, setPick }) => {
  const p = pick[s.item.id] || { on: false, qty: s.suggested_qty, supplier: s.suppliers[0]?.supplier_name || '' };
  const set = (k, v) => setPick({ ...pick, [s.item.id]: { ...p, [k]: v } });
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2" data-testid={`restock-${s.item.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        {!s.suggest_stocking && <input type="checkbox" checked={p.on} onChange={e => set('on', e.target.checked)} className="accent-amber-500 w-4 h-4" data-testid={`restock-check-${s.item.id}`} />}
        <div className="flex-1 min-w-[160px]">
          <p className="text-sm text-white">{s.item.name} <span className="text-slate-500 text-xs">{s.item.part_number}</span></p>
          <p className="text-xs text-slate-400">{s.reason} · stock {qty(s.item.stock_qty)} · {qty(s.monthly_use)}/mois {s.location && <span className="text-amber-300 font-mono ml-1"><MapPin size={10} className="inline" /> {s.location}</span>}</p>
        </div>
        {!s.suggest_stocking && <>
          <input type="number" className={`${invField} w-20`} value={p.qty} onChange={e => set('qty', e.target.value)} data-testid={`restock-qty-${s.item.id}`} />
          <input list={`sups-${s.item.id}`} className={`${invField} w-40`} placeholder="Fournisseur" value={p.supplier} onChange={e => set('supplier', e.target.value)} data-testid={`restock-supplier-${s.item.id}`} />
          <datalist id={`sups-${s.item.id}`}>{s.suppliers.map((x, i) => <option key={i} value={x.supplier_name} />)}</datalist>
        </>}
      </div>
      <SupplierHints sups={s.suppliers} />
    </div>
  );
};

const OrdersList = ({ orders, api, reload }) => {
  const received = async (o) => { try { const r = await api.post(`/orders/${o.id}/received`); toast.success(`Reçue après ${r.lead_days} jour(s)`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = (o) => { navigator.clipboard?.writeText(`Commande ${o.number} — ${o.supplier_name}\n${o.lines.map(l => `${l.quantity} x ${l.part_number || ''} ${l.name}`).join('\n')}`); toast.success('Commande copiée'); };
  if (!orders.length) return null;
  return (
    <div className="space-y-2" data-testid="inventory-orders">
      <p className="text-xs uppercase tracking-wider text-slate-500">Commandes</p>
      {orders.map(o => (
        <div key={o.id} className="flex flex-wrap items-center gap-3 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm" data-testid={`order-${o.id}`}>
          <span className="text-white font-mono">{o.number}</span>
          <span className="text-slate-300 flex-1">{o.supplier_name} · {o.lines.length} pièce(s)</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${o.status === 'sent' ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{o.status === 'sent' ? 'Envoyée' : `Reçue (${o.lead_days} j)`}</span>
          <button onClick={() => copy(o)} className="text-slate-400 hover:text-white" data-testid={`order-copy-${o.id}`}><Copy size={14} /></button>
          {o.status === 'sent' && <button onClick={() => received(o)} className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid={`order-received-${o.id}`}><Check size={14} /> Reçue</button>}
        </div>
      ))}
    </div>
  );
};

export const RestockTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pick, setPick] = useState({});
  const load = () => { api.get('/restock').then(setRows); api.get('/orders').then(setOrders); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const order = async () => {
    const chosen = Object.entries(pick).filter(([, p]) => p.on && Number(p.qty) > 0);
    const bySup = {};
    for (const [id, p] of chosen) {
      if (!p.supplier.trim()) return toast.error('Choisissez un fournisseur pour chaque pièce cochée');
      (bySup[p.supplier.trim()] ||= []).push({ item_id: id, quantity: Number(p.qty) });
    }
    try {
      for (const [supplier_name, lines] of Object.entries(bySup)) await api.post('/orders', { supplier_name, lines });
      toast.success(`${Object.keys(bySup).length} commande(s) créée(s)`); setPick({}); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const count = Object.values(pick).filter(p => p.on).length;

  return (
    <div className="space-y-3" data-testid="inventory-restock-tab">
      {!rows.length && <p className="text-sm text-slate-500 py-6 text-center" data-testid="restock-empty">Aucune pièce à commander pour l'instant.</p>}
      {rows.map(s => <SuggestionRow key={s.item.id} s={s} pick={pick} setPick={setPick} />)}
      {count > 0 && <button onClick={order} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 text-black text-sm font-semibold" data-testid="restock-create-orders"><ShoppingCart size={15} /> Créer les commandes ({count})</button>}
      <OrdersList orders={orders} api={api} reload={load} />
    </div>
  );
};
